import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useParams } from "react-router-dom";

import { useAuth } from "../../contexts/AuthContext";
import Loading from "../../components/Loading";
import { invoiceService, orderUsedPartsService } from "../../services/api";
import { toDateInputValue, todayIsoDate } from "../../utils/orderDates";
import toShort from "../../utils/dateParser";

import orderStyles from "../orders/OrderDetail.module.css";
import styles from "./InvoiceDetail.module.css";

const INVOICE_STATUSES = [
  { value: "draft", label: "Draft" },
  { value: "issued", label: "Issued" },
  { value: "void", label: "Void" },
  { value: "cancelled", label: "Cancelled" },
];

const PAYMENT_STATES = [
  { value: "unpaid", label: "Unpaid" },
  { value: "partial", label: "Partial" },
  { value: "paid", label: "Paid" },
  { value: "overdue", label: "Overdue" },
  { value: "refunded", label: "Refunded" },
];

function emptyLine() {
  return {
    key: `new-${Date.now()}-${Math.random().toString(36).slice(2)}`,
    description: "",
    qty: 1,
    unit: "",
    unitPrice: 0,
    taxRate: 0,
    discountAmount: 0,
  };
}

function mapApiLinesToDraft(lineItems) {
  if (!lineItems || lineItems.length === 0) {
    return [emptyLine()];
  }
  return lineItems.map((row) => ({
    key: `id-${row.id}`,
    description: row.description ?? "",
    qty: row.qty != null ? Number(row.qty) : 1,
    unit: row.unit ?? "",
    unitPrice: row.unitPrice != null ? Number(row.unitPrice) : 0,
    taxRate: row.taxRate != null ? Number(row.taxRate) : 0,
    discountAmount: row.discountAmount != null ? Number(row.discountAmount) : 0,
  }));
}

function draftLinesToPayload(draftLines) {
  return draftLines.map((row, index) => ({
    lineNo: index + 1,
    description: row.description,
    qty: row.qty,
    unit: row.unit || null,
    unitPrice: row.unitPrice,
    taxRate: row.taxRate,
    discountAmount: row.discountAmount,
  }));
}

/** Same row filter as save handler — lines that would be sent to the API. */
function filterPayloadRowsForSave(rows) {
  return rows.filter(
    (row) =>
      String(row.description || "").trim() !== "" ||
      Number(row.qty) !== 0 ||
      Number(row.unitPrice) !== 0
  );
}

function comparablePayloadFromDraft(draftLines) {
  const rows = filterPayloadRowsForSave(draftLinesToPayload(draftLines));
  return JSON.stringify(
    rows.map((row) => ({
      d: String(row.description || "").trim(),
      q: Math.round(Number(row.qty) * 1000) / 1000,
      u: row.unit == null || row.unit === "" ? null : String(row.unit).trim(),
      p: Math.round(Number(row.unitPrice) * 10000) / 10000,
      t: Math.round(Number(row.taxRate) * 10000) / 10000,
      x: Math.round(Number(row.discountAmount) * 10000) / 10000,
    }))
  );
}

function buildInvoiceLineDescriptionFromOrderPart(partRow) {
  const partNumber = partRow.partNumber || "";
  const partDesc = partRow.partDescription || "";
  if (partNumber && partDesc) {
    return `${partNumber} — ${partDesc}`;
  }
  return partNumber || partDesc || "Line item";
}

function formatDateTime(value) {
  if (!value) {
    return "";
  }
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) {
    return String(value);
  }
  return d.toLocaleString();
}

/** Matches the payload sent by Save details (for dirty detection). */
function comparableMetaSnapshot({
  invoiceNumber,
  invoiceDate,
  invoiceStatus,
  paymentState,
  dueDate,
  notes,
}) {
  return JSON.stringify({
    n: String(invoiceNumber ?? "").trim(),
    d: String(invoiceDate ?? "").trim(),
    s: String(invoiceStatus ?? "draft"),
    p: String(paymentState ?? "unpaid"),
    u: String(dueDate ?? "").trim() || null,
    t: String(notes ?? "").trim() || null,
  });
}

function InvoiceDetail() {
  const { invoiceId } = useParams();
  const { hasPermission } = useAuth();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [detail, setDetail] = useState(null);

  const [invoiceNumber, setInvoiceNumber] = useState("");
  const [invoiceDate, setInvoiceDate] = useState("");
  const [invoiceStatus, setInvoiceStatus] = useState("draft");
  const [paymentState, setPaymentState] = useState("unpaid");
  const [dueDate, setDueDate] = useState("");
  const [notes, setNotes] = useState("");

  const [draftLines, setDraftLines] = useState([emptyLine()]);
  const [lineItemsBaselineSerialized, setLineItemsBaselineSerialized] = useState("");
  const [totals, setTotals] = useState({ subtotalExTax: 0, taxTotal: 0, discountTotal: 0, grandTotal: 0 });

  const [orderUsedParts, setOrderUsedParts] = useState([]);
  const [orderUsedPartsError, setOrderUsedPartsError] = useState(null);

  const [metaBaselineSerialized, setMetaBaselineSerialized] = useState("");

  const [metaSaveError, setMetaSaveError] = useState(null);
  const [linesSaveError, setLinesSaveError] = useState(null);
  const [metaSaving, setMetaSaving] = useState(false);
  const [linesSaving, setLinesSaving] = useState(false);

  const canUpdate = hasPermission("component_orders:update");

  const linesDirty = useMemo(() => {
    return comparablePayloadFromDraft(draftLines) !== lineItemsBaselineSerialized;
  }, [draftLines, lineItemsBaselineSerialized]);

  const metaDirty = useMemo(() => {
    return (
      comparableMetaSnapshot({
        invoiceNumber,
        invoiceDate,
        invoiceStatus,
        paymentState,
        dueDate,
        notes,
      }) !== metaBaselineSerialized
    );
  }, [invoiceNumber, invoiceDate, invoiceStatus, paymentState, dueDate, notes, metaBaselineSerialized]);

  const load = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      setNotFound(false);
      const res = await invoiceService.getById(invoiceId);
      const data = res.data.data;
      setDetail(data);
      setInvoiceNumber(data.invoiceNumber || "");
      setInvoiceDate(toDateInputValue(data.invoiceDate) || todayIsoDate());
      setInvoiceStatus(data.invoiceStatus || "draft");
      setPaymentState(data.paymentState || "unpaid");
      setDueDate(data.dueDate ? toDateInputValue(data.dueDate) : "");
      setNotes(data.notes ?? "");
      setMetaBaselineSerialized(
        comparableMetaSnapshot({
          invoiceNumber: data.invoiceNumber || "",
          invoiceDate: toDateInputValue(data.invoiceDate) || todayIsoDate(),
          invoiceStatus: data.invoiceStatus || "draft",
          paymentState: data.paymentState || "unpaid",
          dueDate: data.dueDate ? toDateInputValue(data.dueDate) : "",
          notes: data.notes ?? "",
        })
      );
      const mappedLines = mapApiLinesToDraft(data.lineItems);
      setDraftLines(mappedLines);
      setLineItemsBaselineSerialized(comparablePayloadFromDraft(mappedLines));
      setTotals(data.totals || { subtotalExTax: 0, taxTotal: 0, discountTotal: 0, grandTotal: 0 });

      if (data.componentOrderId) {
        try {
          const up = await orderUsedPartsService.list(data.componentOrderId);
          setOrderUsedParts(Array.isArray(up.data?.data) ? up.data.data : []);
          setOrderUsedPartsError(null);
        } catch (upErr) {
          setOrderUsedParts([]);
          setOrderUsedPartsError(upErr.response?.data?.message || "Could not load order parts");
        }
      } else {
        setOrderUsedParts([]);
        setOrderUsedPartsError(null);
      }
    } catch (err) {
      if (err.response?.status === 404) {
        setNotFound(true);
        setDetail(null);
      } else {
        setError(err.response?.data?.message || "Failed to load invoice");
        setDetail(null);
      }
    } finally {
      setLoading(false);
    }
  }, [invoiceId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleSaveMeta = async () => {
    const num = invoiceNumber.trim();
    if (!num) {
      setMetaSaveError("Invoice number is required");
      return;
    }
    const invDate = invoiceDate.trim();
    if (!invDate) {
      setMetaSaveError("Invoice date is required");
      return;
    }
    setMetaSaveError(null);
    setMetaSaving(true);
    try {
      await invoiceService.update(invoiceId, {
        invoiceNumber: num,
        invoiceDate: invDate,
        invoiceStatus,
        paymentState,
        dueDate: dueDate.trim() || null,
        notes: notes.trim() || null,
      });
      await load();
    } catch (err) {
      setMetaSaveError(
        err.response?.data?.message ||
          (err.response?.status === 409 ? "This invoice number is already in use" : "Failed to save")
      );
    } finally {
      setMetaSaving(false);
    }
  };

  const handleSaveLines = async () => {
    setLinesSaveError(null);
    setLinesSaving(true);
    try {
      const payload = filterPayloadRowsForSave(draftLinesToPayload(draftLines));
      const res = await invoiceService.replaceLineItems(invoiceId, payload);
      setTotals(res.data.data.totals || totals);
      await load();
    } catch (err) {
      setLinesSaveError(err.response?.data?.message || "Failed to save line items");
    } finally {
      setLinesSaving(false);
    }
  };

  const updateLine = (index, field, value) => {
    setDraftLines((prev) => {
      const next = [...prev];
      next[index] = { ...next[index], [field]: value };
      return next;
    });
  };

  const addLine = () => {
    setDraftLines((prev) => [...prev, emptyLine()]);
  };

  const removeLine = (index) => {
    setDraftLines((prev) => {
      if (prev.length <= 1) {
        return [emptyLine()];
      }
      return prev.filter((_, i) => i !== index);
    });
  };

  const addOrderPartAsLine = useCallback((partRow) => {
    const description = buildInvoiceLineDescriptionFromOrderPart(partRow);
    const qtyRaw = partRow.quantity != null ? Number(partRow.quantity) : 1;
    const qty = Number.isFinite(qtyRaw) && qtyRaw > 0 ? qtyRaw : 1;
    const priceRaw = partRow.unitPriceEur != null ? Number(partRow.unitPriceEur) : 0;
    const unitPrice = Number.isFinite(priceRaw) && priceRaw >= 0 ? priceRaw : 0;

    setDraftLines((prev) => {
      const isBlankSingle =
        prev.length === 1 &&
        !String(prev[0].description || "").trim() &&
        Number(prev[0].qty) === 1 &&
        Number(prev[0].unitPrice) === 0;
      const line = {
        key: `new-${Date.now()}-${Math.random().toString(36).slice(2)}`,
        description,
        qty,
        unit: "",
        unitPrice,
        taxRate: 0,
        discountAmount: 0,
      };
      if (isBlankSingle) {
        return [line];
      }
      return [...prev, line];
    });
  }, []);

  if (loading) {
    return <Loading loadingText="Loading invoice..." />;
  }

  if (notFound) {
    return (
      <div className={orderStyles.container}>
        <div className={orderStyles.card}>
          <div className={orderStyles.notFound}>
            <p>Invoice not found.</p>
            <Link to="/invoices" className={orderStyles.backLink}>
              Back to invoices
            </Link>
          </div>
        </div>
      </div>
    );
  }

  if (error || !detail) {
    return (
      <div className={orderStyles.container}>
        <div className={orderStyles.error}>{error || "Something went wrong."}</div>
        <Link to="/invoices" className={orderStyles.backLink}>
          Back to invoices
        </Link>
      </div>
    );
  }

  return (
    <div className={orderStyles.container}>
      <header className={orderStyles.orderPageHeader}>
        <div className={orderStyles.orderHeaderRow}>
          <h1 className={orderStyles.orderTitle}>{detail.invoiceNumber || "Invoice"}</h1>
          <div className={orderStyles.orderHeaderCenter}>
            <span className={styles.statusBadge}>{detail.invoiceStatus || "draft"}</span>
            <span className={styles.paymentBadge}>{detail.paymentState || "unpaid"}</span>
          </div>
          <div className={orderStyles.orderHeaderSpacer} aria-hidden />
        </div>
        {detail.orderInternalNo ? (
          <p className={orderStyles.orderSubtitle}>
            Order{" "}
            <Link to={`/orders/${detail.componentOrderId}`}>{detail.orderInternalNo}</Link>
            {detail.customerName ? ` · ${detail.customerName}` : ""}
          </p>
        ) : null}
      </header>

      <div className={orderStyles.card}>
        <div className={orderStyles.headerBar}>
          <Link to="/invoices" className={orderStyles.backLink}>
            ← Back to invoices
          </Link>
          <div className={orderStyles.actions}>
            <Link to={`/orders/${detail.componentOrderId}`} className={orderStyles.backLink}>
              View order
            </Link>
          </div>
        </div>

        <div className={orderStyles.body}>
          <div className={orderStyles.historyOrderMeta}>
            <span>Created by {detail.createdBy || "—"}</span>
            {" · "}
            <span>Created {formatDateTime(detail.createdAt)}</span>
            {detail.updatedAt ? (
              <>
                {" · "}
                <span>Updated {formatDateTime(detail.updatedAt)}</span>
              </>
            ) : null}
          </div>

          <h2 className={orderStyles.historyTitle}>Invoice details</h2>
          {metaSaveError && <div className={orderStyles.error}>{metaSaveError}</div>}

          <dl className={orderStyles.detailGrid}>
            <div className={orderStyles.detailItem}>
              <dt>Invoice number</dt>
              <dd>
                {canUpdate ? (
                  <input
                    className={styles.lineInput}
                    value={invoiceNumber}
                    onChange={(e) => setInvoiceNumber(e.target.value)}
                    autoComplete="off"
                  />
                ) : (
                  detail.invoiceNumber
                )}
              </dd>
            </div>
            <div className={orderStyles.detailItem}>
              <dt>Invoice date</dt>
              <dd>
                {canUpdate ? (
                  <input
                    type="date"
                    className={styles.lineInput}
                    value={invoiceDate}
                    onChange={(e) => setInvoiceDate(e.target.value)}
                  />
                ) : (
                  toShort(detail.invoiceDate)
                )}
              </dd>
            </div>
            <div className={orderStyles.detailItem}>
              <dt>Due date</dt>
              <dd>
                {canUpdate ? (
                  <input
                    type="date"
                    className={styles.lineInput}
                    value={dueDate}
                    onChange={(e) => setDueDate(e.target.value)}
                  />
                ) : (
                  detail.dueDate ? toShort(detail.dueDate) : "—"
                )}
              </dd>
            </div>
            <div className={orderStyles.detailItem}>
              <dt>Status</dt>
              <dd>
                {canUpdate ? (
                  <select
                    className={styles.select}
                    value={invoiceStatus}
                    onChange={(e) => setInvoiceStatus(e.target.value)}
                  >
                    {INVOICE_STATUSES.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                ) : (
                  detail.invoiceStatus
                )}
              </dd>
            </div>
            <div className={orderStyles.detailItem}>
              <dt>Payment</dt>
              <dd>
                {canUpdate ? (
                  <select
                    className={styles.select}
                    value={paymentState}
                    onChange={(e) => setPaymentState(e.target.value)}
                  >
                    {PAYMENT_STATES.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                ) : (
                  detail.paymentState
                )}
              </dd>
            </div>
          </dl>

          <div className={`${orderStyles.detailItem} ${orderStyles.detailItemFull}`} style={{ marginTop: 16 }}>
            <div className={styles.metaLabel}>Notes</div>
            {canUpdate ? (
              <textarea
                className={styles.notesArea}
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Internal notes…"
              />
            ) : (
              <div className={orderStyles.remarksBody}>{detail.notes || "—"}</div>
            )}
          </div>

          {canUpdate && metaDirty && (
            <div className={styles.sectionActions}>
              <button
                type="button"
                className={styles.saveDirtyButton}
                onClick={handleSaveMeta}
                disabled={metaSaving}
              >
                {metaSaving ? "Saving…" : "Save details"}
              </button>
            </div>
          )}
        </div>
      </div>

      <div className={orderStyles.historyCard}>
        <h2 className={orderStyles.historyTitle}>Line items</h2>
        {linesSaveError && <div className={orderStyles.error}>{linesSaveError}</div>}

        <div className={styles.orderPartsPanel}>
          <h3 className={styles.subsectionTitle}>Work order parts and services</h3>
          <p className={styles.orderPartsHint}>
            Parts recorded on the linked order.
            {canUpdate
              ? ' "Add to lines" copies quantity and unit price from the order into the invoice table (edit before saving if needed).'
              : ""}
          </p>
          {orderUsedPartsError && <div className={orderStyles.error}>{orderUsedPartsError}</div>}
          {!orderUsedPartsError && orderUsedParts.length === 0 ? (
            <p className={styles.orderPartsEmpty}>No parts on this order yet.</p>
          ) : null}
          {!orderUsedPartsError && orderUsedParts.length > 0 ? (
            <div className={styles.orderPartsTableWrap}>
              <table className={styles.orderPartsTable}>
                <thead>
                  <tr>
                    <th>Part number</th>
                    <th>Description</th>
                    <th className={styles.orderPartsNum}>Qty</th>
                    <th className={styles.orderPartsNum}>Unit EUR</th>
                    <th className={styles.orderPartsNum}>Line EUR</th>
                    {canUpdate && <th />}
                  </tr>
                </thead>
                <tbody>
                  {orderUsedParts.map((row) => (
                    <tr key={row.id}>
                      <td>{row.partNumber || "—"}</td>
                      <td>{row.partDescription || "—"}</td>
                      <td className={styles.orderPartsNum}>{row.quantity != null ? Number(row.quantity) : "—"}</td>
                      <td className={styles.orderPartsNum}>
                        {row.unitPriceEur != null ? Number(row.unitPriceEur).toFixed(4) : "—"}
                      </td>
                      <td className={styles.orderPartsNum}>
                        {row.lineTotalEur != null ? Number(row.lineTotalEur).toFixed(4) : "—"}
                      </td>
                      {canUpdate && (
                        <td className={styles.orderPartsActions}>
                          <button type="button" className={styles.addFromOrderBtn} onClick={() => addOrderPartAsLine(row)}>
                            Add to lines
                          </button>
                        </td>
                      )}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : null}
        </div>

        <div className={styles.lineTableWrap}>
          <table className={styles.lineTable}>
            <thead>
              <tr>
                <th>#</th>
                <th>Description</th>
                <th>Qty</th>
                <th>Unit</th>
                <th>Unit price</th>
                <th>Tax %</th>
                <th>Discount</th>
                {canUpdate && <th />}
              </tr>
            </thead>
            <tbody>
              {draftLines.map((row, index) => (
                <tr key={row.key}>
                  <td>{index + 1}</td>
                  <td>
                    {canUpdate ? (
                      <input
                        className={styles.lineInput}
                        value={row.description}
                        onChange={(e) => updateLine(index, "description", e.target.value)}
                        placeholder="Description"
                      />
                    ) : (
                      row.description || "—"
                    )}
                  </td>
                  <td>
                    {canUpdate ? (
                      <input
                        type="number"
                        step="0.001"
                        className={`${styles.lineInput} ${styles.lineInputNarrow}`}
                        value={row.qty}
                        onChange={(e) => updateLine(index, "qty", e.target.value === "" ? "" : Number(e.target.value))}
                      />
                    ) : (
                      row.qty
                    )}
                  </td>
                  <td>
                    {canUpdate ? (
                      <input
                        className={`${styles.lineInput} ${styles.lineInputNarrow}`}
                        value={row.unit}
                        onChange={(e) => updateLine(index, "unit", e.target.value)}
                        placeholder="—"
                      />
                    ) : (
                      row.unit || "—"
                    )}
                  </td>
                  <td>
                    {canUpdate ? (
                      <input
                        type="number"
                        step="0.0001"
                        className={`${styles.lineInput} ${styles.lineInputNarrow}`}
                        value={row.unitPrice}
                        onChange={(e) =>
                          updateLine(index, "unitPrice", e.target.value === "" ? "" : Number(e.target.value))
                        }
                      />
                    ) : (
                      row.unitPrice
                    )}
                  </td>
                  <td>
                    {canUpdate ? (
                      <input
                        type="number"
                        step="0.01"
                        className={`${styles.lineInput} ${styles.lineInputNarrow}`}
                        value={row.taxRate}
                        onChange={(e) =>
                          updateLine(index, "taxRate", e.target.value === "" ? "" : Number(e.target.value))
                        }
                      />
                    ) : (
                      row.taxRate
                    )}
                  </td>
                  <td>
                    {canUpdate ? (
                      <input
                        type="number"
                        step="0.0001"
                        className={`${styles.lineInput} ${styles.lineInputNarrow}`}
                        value={row.discountAmount}
                        onChange={(e) =>
                          updateLine(index, "discountAmount", e.target.value === "" ? "" : Number(e.target.value))
                        }
                      />
                    ) : (
                      row.discountAmount
                    )}
                  </td>
                  {canUpdate && (
                    <td className={styles.rowActions}>
                      <button type="button" className={styles.removeLineBtn} onClick={() => removeLine(index)}>
                        Remove
                      </button>
                    </td>
                  )}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {canUpdate && (
          <>
            <button type="button" className={styles.addLineBtn} onClick={addLine}>
              + Add line
            </button>
            {linesDirty && (
              <div className={styles.sectionActions}>
                <button
                  type="button"
                  className={styles.saveDirtyButton}
                  onClick={handleSaveLines}
                  disabled={linesSaving}
                >
                  {linesSaving ? "Saving…" : "Save line items"}
                </button>
              </div>
            )}
          </>
        )}

        <div className={styles.totalsCard}>
          <div>
            <span className={styles.totalsLabel}>Subtotal (ex tax)</span>
            <span className={styles.totalsValue}>{Number(totals.subtotalExTax).toFixed(4)}</span>
          </div>
          <div>
            <span className={styles.totalsLabel}>Tax</span>
            <span className={styles.totalsValue}>{Number(totals.taxTotal).toFixed(4)}</span>
          </div>
          <div>
            <span className={styles.totalsLabel}>Discounts</span>
            <span className={styles.totalsValue}>{Number(totals.discountTotal).toFixed(4)}</span>
          </div>
          <div>
            <span className={styles.totalsLabel}>Total</span>
            <span className={styles.totalsValue}>{Number(totals.grandTotal).toFixed(4)}</span>
          </div>
        </div>
      </div>
    </div>
  );
}

export default InvoiceDetail;
